import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_SpendFunctionInputRelationshipRow,
    WB_SpendFunctionRow, WB_SpendFunctionTermRelationshipRow,
} from "../PA.Core/PAdbSchemaRows";
import {IwbSpendFunction} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Spend_Function, esWB_Spend_Function_Input_Relationship, esWB_Spend_Function_Term_Relationship
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {PAEntity} from "../PA.Core/PAEntity";
import { ErrorMessage_restate, WarningMessage_restate } from "../PA.Core/Basic/MessageUtils";

export class SpendFunctionBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_SpendFunction: WB_SpendFunctionRow;

    _bIsIdEditable: boolean;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Spend Function'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_SpendFunction = null;
        this._bIsIdEditable = true
    }

    get WB_SpendFunction(): WB_SpendFunctionRow {
        return this.drWB_SpendFunction;
    }

    get bIsIdEditable(): boolean {
        return this._bIsIdEditable
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Spend Function");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new Spend Function";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Spend_Function)
                .then((newSpendFunctionRow: WB_SpendFunctionRow) => {
                    newSpendFunctionRow.setDefaults();
                    self.drWB_SpendFunction = newSpendFunctionRow;
                    self.dbContext.add(esWB_Spend_Function, newSpendFunctionRow);
                    self.Identity.setAll(self.WB_SpendFunction.cif.WB_SPEND_FUNCTION_ID, self.WB_SpendFunction.cif.SPEND_FUNCTION_ID, self.WB_SpendFunction.cif.SPEND_FUNCTION_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_SpendFunction, 'WB_SPEND_FUNCTION_ID', 'SPEND_FUNCTION_ID', 'SPEND_FUNCTION_NAME');
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    newSpendFunctionRow.cif.FUNCTION_USAGE = "Complex"
                    newSpendFunctionRow.cif.AMOUNT_LOCATION = "Header"
                    self._bIsIdEditable = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                let sError = "Spend Function already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Spend Function";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = SpendFunctionBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = SpendFunctionBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newSpendFunctionRow: any = recordResolver.get(esWB_Spend_Function, anId);

                    if (_.isEmpty(newSpendFunctionRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_SpendFunction = newSpendFunctionRow;
                    self.Identity.setAll(self.WB_SpendFunction.cif.WB_SPEND_FUNCTION_ID, self.WB_SpendFunction.cif.SPEND_FUNCTION_ID, self.WB_SpendFunction.cif.SPEND_FUNCTION_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_SpendFunction, 'WB_SPEND_FUNCTION_ID', 'SPEND_FUNCTION_ID', 'SPEND_FUNCTION_NAME');
                })
                .then((ignore: any ) => {
                    // self._bIsIdEditable = !bExist
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {

        const self = this;
        const mp = self.ValidateMessageProvider
        const pp = self.ValidateProgressProvider


        mp.clearMessages();
        pp.start("Validating " + self.WB_SpendFunction.cif.SPEND_FUNCTION_NAME + "(" + self.WB_SpendFunction.cif.SPEND_FUNCTION_ID + ")");

        self.ValidateOptions = options || {};
        let  dr = self.drWB_SpendFunction.cif

        try {

            ErrorMessage_restate(mp, (self.dbContext == undefined),"dbCtx", "Unable to determine dbContext to Validate Spend Function.")

            ErrorMessage_restate(mp, (dr.FUNCTION_DEFN_SRC.replace(" ", "") === ""),"formula",
                "The java expression cannot be empty")

            ErrorMessage_restate(mp, (_.isEmpty(dr.FUNCTION_USAGE)),"usage",
                "The Usage cannot be empty")

            if(dr.FUNCTION_USAGE == "Complex"){
                WarningMessage_restate(mp, !(dr.WB_SPEND_FUNCTION_ID_SPECIFIC > 0), 'SpcFnc','Specific Function needed')
            } else if(dr.FUNCTION_USAGE == "RateAmt") {
                ErrorMessage_restate(mp, _.isEmpty(dr.AMOUNT_LOCATION), 'AmtLoc','Amount Location required')
            }


            self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
            self.ValidateProgressProvider.finish();
        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self;
        }

        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        const wbSpendFunction = <IwbSpendFunction>self.drWB_SpendFunction;
        try {
            self.SaveProgressProvider.start("Saving " + wbSpendFunction.SPEND_FUNCTION_NAME + "(" + wbSpendFunction.SPEND_FUNCTION_ID + ")");
            self._bIsSaved = false;
            self.SaveOptions = options || {};

            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext to Save Spend Function";
                self.SaveProgressProvider.exception(sError);
                return
            }

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }


            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            await saveFunction(esWB_Spend_Function, self.drWB_SpendFunction, {})

            let tasks: Promise<any>[] = []

            tasks.push(saveFunction(esWB_Spend_Function_Input_Relationship, self.drWB_SpendFunction.WB_SpendFunctionInputRelationshipRows))
            tasks.push(saveFunction(esWB_Spend_Function_Term_Relationship, self.drWB_SpendFunction.WB_SpendFunctionTermRelationshipRows))
            await Promise.all(tasks)
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }

            self._bIsIdEditable = false
            self.SaveProgressProvider.finish();
            return self;
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return (self)
        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbSpendFunction = <IwbSpendFunction>self.drWB_SpendFunction;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbSpendFunction.SPEND_FUNCTION_NAME + "(" + wbSpendFunction.SPEND_FUNCTION_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Model Predictor

            // 2. If Valid, mark all the records for deletion.
            self.drWB_SpendFunction.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    addSpendFunctionInputRecord(): Promise<WB_SpendFunctionInputRelationshipRow> {
        const self = this;
        return new Promise<WB_SpendFunctionInputRelationshipRow>((resolve, error) => {
            var found = false

            return self.dbContext.createRecordWithKey(esWB_Spend_Function_Input_Relationship)
                .then((aNewRow: WB_SpendFunctionInputRelationshipRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_SPEND_FUNCTION_ID = self.drWB_SpendFunction.cif.WB_SPEND_FUNCTION_ID;
                    const filteredRecords = _.filter(self.drWB_SpendFunction.WB_SpendFunctionInputRelationshipRows, (aRow: WB_SpendFunctionInputRelationshipRow) => aRow.isActive)
                    if (filteredRecords.length) {
                        const record = filteredRecords[(filteredRecords).length - 1]
                        aNewRow.cif.INPUT_SEQ = record.cif.INPUT_SEQ + 1
                    }
                    self.dbContext.add(esWB_Spend_Function_Input_Relationship, aNewRow);
                    resolve(aNewRow)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeSpndInput(aSpnFnInputId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            let delIndex: number
            _.forEach(self.drWB_SpendFunction.WB_SpendFunctionInputRelationshipRows, (x: WB_SpendFunctionInputRelationshipRow, index: number) => {
                if (x.isActive && x.cif.INPUT_SEQ == aSpnFnInputId) {
                    delIndex = x.cif.INPUT_SEQ
                    x.markDeleted()
                }
            })
            _.forEach(_.sortBy(_.filter(self.drWB_SpendFunction.WB_SpendFunctionInputRelationshipRows, aRow => aRow.isActive),
                ['INPUT_SEQ'], ['asc']),
                (y: WB_SpendFunctionInputRelationshipRow, idx, arr) => {
                    if (y.cif.INPUT_SEQ > delIndex) {
                        y.cif.INPUT_SEQ = y.cif.INPUT_SEQ - 1
                    }
                })
            resolve()
        })
    }

    addSpendFunctionTermRecord(): Promise<WB_SpendFunctionTermRelationshipRow> {
        const self = this;
        return new Promise<WB_SpendFunctionTermRelationshipRow>((resolve, error) => {
            var found = false

            return self.dbContext.createRecordWithKey(esWB_Spend_Function_Term_Relationship)
                .then((aNewRow: WB_SpendFunctionTermRelationshipRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_SPEND_FUNCTION_ID = self.drWB_SpendFunction.cif.WB_SPEND_FUNCTION_ID;
                    const filteredRecords = _.filter(self.drWB_SpendFunction.WB_SpendFunctionTermRelationshipRows, (aRow: WB_SpendFunctionTermRelationshipRow) => aRow.isActive)
                    if (filteredRecords.length) {
                        const record = filteredRecords[(filteredRecords).length - 1]
                        aNewRow.cif.TERM_SEQ = record.cif.TERM_SEQ + 1
                    }
                    self.dbContext.add(esWB_Spend_Function_Term_Relationship, aNewRow);
                    resolve(aNewRow)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeSpndTerm(aSpnFnTermId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            let delIndex: number
            _.forEach(self.drWB_SpendFunction.WB_SpendFunctionTermRelationshipRows, (x: WB_SpendFunctionTermRelationshipRow, index: number) => {
                if (x.isActive && x.cif.TERM_SEQ == aSpnFnTermId) {
                    delIndex = x.cif.TERM_SEQ
                    x.markDeleted()
                }
            })
            _.forEach(_.sortBy(_.filter(self.drWB_SpendFunction.WB_SpendFunctionTermRelationshipRows, aRow => aRow.isActive),
                ['TERM_SEQ'], ['asc']),
                (y: WB_SpendFunctionTermRelationshipRow, idx, arr) => {
                    if (y.cif.TERM_SEQ > delIndex) {
                        y.cif.TERM_SEQ = y.cif.TERM_SEQ - 1
                    }
                })
            resolve()
        })
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const spendFunction_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, spendFunction_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Spend_Function, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(SpendFunctionBO, "SpendFunctionBO", "Spend Function")
